/**
 * The search box for a server-rendered DIRECTORY page (blog, estates).
 *
 * A plain GET form, deliberately. These pages are server-rendered and
 * indexable, so a search result is just another URL of the same page:
 * submitting navigates, the SSR loader answers, and the result is shareable,
 * bookmarkable and back-button-correct with no client fetching at all. The
 * listings omnibox solves a different problem — suggestions as you type over a
 * live map — and these surfaces would only pay its cost.
 *
 * One component rather than one per directory: blog and estates differ only in
 * their action URL, their strings, and which filter rides along as a hidden
 * field. Copying it per surface is how the two would drift (the buy/rent pages
 * are the standing example in this repo).
 *
 * THE ACTION IS A `Route`, LOCALIZED HERE. A native form's `action` is a raw
 * URL the browser resolves absolute, so `action="/estates"` submitted from
 * `/zh-CN/estates` landed on the DEFAULT locale — the ADR 0142 locale-drop
 * class one door over from `Link`, invisible to `no-internal-anchor`'s literal
 * check because the string arrived through a prop. The prop is now the branded
 * `Route` (a string is a compile error) and the locale is re-applied by the
 * routing seam's `useLocalizedHref`, exactly as `Link` does for `href`.
 */
import { useLocalizedHref } from '@/shared/i18n/routing';
import type { Route } from '@letsgethome/routes';
import type { LocalizedText } from '@letsgethome/types/branded';
import { Button } from '@letsgethome/ui/button';
import { Search, X } from 'lucide-react';

export interface DirectorySearchProps {
  /** Where the form submits — the directory's own index route (a `Route`, never a string). */
  action: Route;
  /** The wire param name for the query (the codec's, never a literal here). */
  name: string;
  /** What the server actually searched for (echoed by the loader). */
  query: string;
  /**
   * Filters that must survive a search, as `{ paramName: value }`. Rendered as
   * hidden inputs, because a GET form submits ONLY its own fields — anything
   * omitted here is silently dropped from the URL on submit.
   */
  preserve?: Record<string, string | undefined>;
  labels: {
    label: LocalizedText;
    placeholder: LocalizedText;
    submit: LocalizedText;
    clear: LocalizedText;
  };
  className?: string;
}

export function DirectorySearch({
  action,
  name,
  query,
  preserve,
  labels,
  className,
}: DirectorySearchProps) {
  const localizedAction = useLocalizedHref(action);
  return (
    <form
      method="get"
      action={localizedAction}
      role="search"
      className={className ?? 'mb-8 flex gap-2'}
    >
      {Object.entries(preserve ?? {}).map(([key, value]) =>
        value ? <input key={key} type="hidden" name={key} value={value} /> : null
      )}
      <div className="relative flex-1">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          type="search"
          name={name}
          defaultValue={query}
          aria-label={labels.label}
          placeholder={labels.placeholder}
          className="h-10 w-full rounded-md border border-border bg-background pl-9 pr-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <Button type="submit">{labels.submit}</Button>
      {query ? (
        // Submitting the same form with an empty `q` clears the search while
        // keeping every preserved filter — no second route, no client state.
        <Button type="submit" variant="ghost" name={name} value="" aria-label={labels.clear}>
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      ) : null}
    </form>
  );
}
